import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { ClerkProvider, useAuth as useClerkAuth, useUser } from '@clerk/clerk-react'
import api from '@/api/client'
import { setAuthTokenGetter } from '@/lib/authToken'

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || ''

const AuthContext = createContext({
  ready: false,
  isAuthenticated: false,
  user: null,
  clerkEnabled: false,
  signOut: async () => {},
})

export function useAppAuth() {
  return useContext(AuthContext)
}

function ClerkBridge({ children }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth()
  const { user: clerkUser } = useUser()
  const [remoteUser, setRemoteUser] = useState(null)
  const [remoteLoaded, setRemoteLoaded] = useState(false)

  useEffect(() => {
    setAuthTokenGetter(() => getToken())
  }, [getToken])

  useEffect(() => {
    let alive = true
    if (!isLoaded || !isSignedIn) {
      setRemoteUser(null)
      setRemoteLoaded(isLoaded)
      return () => { alive = false }
    }
    api.get('/auth/me')
      .then((r) => { if (alive) setRemoteUser(r.data.user) })
      .catch(() => { if (alive) setRemoteUser(null) })
      .finally(() => { if (alive) setRemoteLoaded(true) })
    return () => { alive = false }
  }, [isLoaded, isSignedIn, clerkUser?.id])

  const value = useMemo(() => {
    const role = remoteUser?.role || clerkUser?.publicMetadata?.role || 'reviewer'
    return {
      ready: isLoaded && remoteLoaded,
      isAuthenticated: !!isSignedIn,
      clerkEnabled: true,
      user: isSignedIn
        ? {
            sub: remoteUser?.sub || clerkUser?.id,
            email: remoteUser?.email || clerkUser?.primaryEmailAddress?.emailAddress,
            name: remoteUser?.name || clerkUser?.fullName || clerkUser?.username,
            role,
          }
        : null,
      signOut: () => signOut({ redirectUrl: '/' }),
    }
  }, [isLoaded, remoteLoaded, isSignedIn, remoteUser, clerkUser, signOut])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function AuthProvider({ children }) {
  if (!PUBLISHABLE_KEY) {
    // Clerk not configured yet — run open (matches the backend's dev mode).
    const devValue = {
      ready: true,
      isAuthenticated: true,
      clerkEnabled: false,
      user: { sub: 'dev', name: 'Dev (auth disabled)', role: 'admin' },
      signOut: async () => {},
    }
    return <AuthContext.Provider value={devValue}>{children}</AuthContext.Provider>
  }
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <ClerkBridge>{children}</ClerkBridge>
    </ClerkProvider>
  )
}
